/**
 * ClosingDisclosureData - Complete unified data structure for the Closing Disclosure form
 *
 * This interface represents all data across all 5 pages of the CD form.
 */

// Re-export page data types for external use
export type { CDPage1Data } from '../components/pages/CDPage1';
export type { CDPage2Data } from '../components/pages/CDPage2';
export type { CDPage3Data } from '../components/pages/CDPage3';
export type { CDPage4Data } from '../components/pages/CDPage4';
export type { CDPage5Data } from '../components/pages/CDPage5';

import type { CDPage1Data } from '../components/pages/CDPage1';
import type { CDPage2Data } from '../components/pages/CDPage2';
import type { CDPage3Data } from '../components/pages/CDPage3';
import type { CDPage4Data } from '../components/pages/CDPage4';
import type { CDPage5Data } from '../components/pages/CDPage5';

/**
 * Complete Closing Disclosure data structure
 */
export interface ClosingDisclosureData {
  /** Loan/Transaction ID */
  loanId?: string;
  /** Form ID (e.g., "CFPB Form H-25(A)") */
  formId?: string;
  /** Total number of pages */
  totalPages?: number;
  /** Page 1: Header, Loan Terms, Projected Payments, Costs at Closing */
  page1: Partial<CDPage1Data>;
  /** Page 2: Closing Cost Details (Sections A-J) */
  page2: Partial<CDPage2Data>;
  /** Page 3: Calculating Cash to Close, Summaries of Transactions */
  page3: Partial<CDPage3Data>;
  /** Page 4: Loan Disclosures, Escrow Account, Adjustable Tables */
  page4: Partial<CDPage4Data>;
  /** Page 5: Loan Calculations, Other Disclosures, Contact Info, Confirm Receipt */
  page5: Partial<CDPage5Data>;
}

/**
 * Context value for the ClosingDisclosure form
 */
export interface ClosingDisclosureContextValue {
  /** Complete form data */
  formData: ClosingDisclosureData;
  /** Whether the form is in edit mode */
  editable: boolean;
  /** Whether the form is currently saving/loading */
  isLoading: boolean;
  /** Any error message */
  error: string | null;
  /** Update a specific field by path */
  updateField: (path: string, value: unknown) => void;
  /** Update an entire page's data */
  updatePage: <K extends keyof ClosingDisclosureData>(
    page: K,
    data: ClosingDisclosureData[K],
  ) => void;
  /** Reset form to initial data */
  resetForm: () => void;
  /** Recalculate all derived values */
  recalculate: () => void;
}

/**
 * Default empty Closing Disclosure data
 */
export const DEFAULT_CLOSING_DISCLOSURE_DATA: ClosingDisclosureData = {
  loanId: '',
  formId: 'CFPB Form H-25(A)',
  totalPages: 5,
  page1: {
    header: {},
    loanTerms: {},
    projectedPayments: {},
    costsAtClosing: {},
  },
  page2: {
    loanCosts: {},
    otherCosts: {},
    totalClosingCosts: {},
  },
  page3: {
    calculatingCashToClose: {},
    summariesOfTransactions: {},
  },
  page4: {
    loanDisclosures: {},
    escrowAccount: {},
  },
  page5: {
    loanCalculations: {},
    otherDisclosures: {},
    contactInformation: {},
    confirmReceipt: {},
  },
};
